import { useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  Divider,
  TextField,
} from '@mui/material';
import Rating from '@mui/material/Rating';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import { IComandaEtapaReunioes } from '../../../commons/types/crm';
import { useTranslation } from '../../../commons/useTranslasion';
import { esES, ptBR } from '@mui/material/locale';

export interface IAprovar {
  open: boolean;
  handleClose: () => void;
  handleApprove: () => void;
  handleReject: () => void;
  nota: number;
  setNota: (value: number) => void;
  comentario: string;
  setComentario: (value: string) => void;
  comentarioReprovar: string;
  setComentarioReprovar: (value: string) => void;
  selectedReuniao?: IComandaEtapaReunioes;
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '1px solid #000',
  borderRadius: '20px',
  boxShadow: 24,
  p: 4,
};

const Aprovar: React.FC<IAprovar> = ({
  open,
  handleClose,
  handleApprove,
  handleReject,
  nota,
  setNota,
  comentario,
  setComentario,
  comentarioReprovar,
  setComentarioReprovar,
  selectedReuniao
}) => {
  const { translate, language } = useTranslation();
  const lang = language ?? "pt-br";
  let locale = ptBR;
  switch (lang.slice(0, 2)) {
    case "es":
      locale = esES
      break;
  }
  
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [erroComentario, setErroComentario] = useState(false);

  if (!selectedReuniao) {
    return null;
  }

  const consultor = selectedReuniao?.consultorResponsavel?.nome || translate('nomeDoConsultor');
  const data = new Date(selectedReuniao.dtPrevista as any).toLocaleDateString();
  const dataInicio = selectedReuniao.dtRealizadaInicio 
    ? `${data} ${new Date(selectedReuniao.dtRealizadaInicio as any).toLocaleTimeString()}`
    : translate('naoIniciada');
  const dataFim = selectedReuniao.dtRealizadaFim
    ? `${data} ${new Date(selectedReuniao.dtRealizadaFim as any).toLocaleTimeString()}`
    : translate('naoFinalizada');

  const rejeitarReuniao = () => {
    if (!comentarioReprovar.trim()) {
      setErroComentario(true);
      return;
    }
    setErroComentario(false);
    setOpenConfirmation(false);
    handleReject();
  };

  const handleConfirmationClose = () => {
    setErroComentario(false);
    setComentarioReprovar('');
    setOpenConfirmation(false);
  };

  return (
    <>
      <Modal open={open} onClose={handleClose} sx={{ borderRadius: '20px' }}>
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2" gutterBottom>
            {translate('aprovarReuniao')}
          </Typography>

          <Divider />

          <Grid container spacing={2} sx={{ marginTop: '10px' }}>
            <Grid item xs={12}>
              <Typography>
                <strong>{translate('consultor')}:</strong> {consultor}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography>
                <strong>{translate('dataInicio')}:</strong> {dataInicio}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography>
                <strong>{translate('dataFim')}:</strong> {dataFim}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ marginTop: '10px' }} />

          <Grid container spacing={1} sx={{ marginTop: '10px' }}>
            <Grid item xs={12}>
              <Typography>
                <strong>{translate('avaliar')}:</strong>
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography>{translate('nota')}:</Typography>
              <Rating
                name="nota"
                value={nota}
                onChange={(_, newValue) => setNota(newValue || 0)}
                max={5}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={translate('comentario')}
                fullWidth
                multiline
                rows={3}
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} justifyContent="center" sx={{ marginTop: '20px' }}>
            <Grid item>
              <Button variant="contained" color="success" onClick={handleApprove}>
                {translate('validarReuniao')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      <Modal open={openConfirmation} onClose={handleConfirmationClose}>
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2" gutterBottom>
            {translate('confirmacao')}
          </Typography>

          <Divider />

          <Grid container spacing={2} sx={{ marginTop: '10px' }}>
            <Grid item xs={12}>
              <Typography>{translate('motivoReprovar')}</Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ marginTop: '10px' }}>
            <Grid item xs={12}>
              <TextField
                label={translate('comentario')}
                fullWidth
                required
                multiline
                rows={3}
                value={comentarioReprovar}
                onChange={(e) => setComentarioReprovar(e.target.value)}
                error={erroComentario && comentarioReprovar.trim() === ''}
                helperText={
                  erroComentario && comentarioReprovar.trim() === ''
                    ? translate('comentarioObrigatorio')
                    : ''
                }
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} justifyContent="center" sx={{ marginTop: '20px' }}>
            <Grid item>
              <Button variant="outlined" color="info" onClick={handleConfirmationClose}>
                {translate('cancelar')}
              </Button>
            </Grid>
            <Grid item>
              <Button variant="contained" color="error" onClick={rejeitarReuniao}>
                {translate('confirmar')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
};

export default Aprovar;